import { useEffect, useMemo, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { t } from '../lib/i18n';
import { messageFor } from '../lib/errors';
import { Badge } from '../ui/Page';
import { MemberSelect } from '../ui/UserSelect';
import {
  acceptProposal,
  createEvent,
  deleteEvent,
  getEvent,
  inviteGuests,
  listColors,
  respondToEvent,
  updateEvent,
  type CalendarView,
  type EventDetail,
  type EventStatus,
  type EventVisibility,
  type Frequency,
  type RuleInput,
  type SeriesScope,
} from './api';

/**
 * One event, with everything a person can decide about it.
 *
 * It is one form for two jobs — writing a new meeting and changing an existing one — because they
 * are the same decisions and a product with two different forms ends up with two different sets of
 * fields. A draft (no id yet) simply has nothing to load.
 *
 * The part worth reading carefully is the series. Changing one occurrence of a recurring meeting is
 * three different intentions, and the dialog asks which rather than guessing: only this one, this
 * and the ones after it, or the whole series. Guessing is how somebody moves one week's stand-up
 * and finds they have moved a year of them.
 *
 * A real `<dialog showModal>`, not a panel styled to look like one. The browser then owns the parts
 * that are tedious and easy to get subtly wrong: the backdrop, the focus trap, Escape, and taking
 * the rest of the page out of the accessibility tree while it is open. Writing a meeting is a
 * modal act — nothing else on the screen is useful until it is saved or abandoned — so the markup
 * should say so.
 */

export interface EventDraft {
  startsAt: Date;
  endsAt: Date;
  calendarId?: string;
  allDay?: boolean;
}

export function EventDialog({
  eventId,
  draft,
  calendars,
  onClose,
}: {
  /** An existing event, or null when writing a new one. */
  eventId: string | null;
  draft: EventDraft | null;
  calendars: readonly CalendarView[];
  onClose: () => void;
}): React.JSX.Element {
  const client = useQueryClient();
  const dialog = useRef<HTMLDialogElement>(null);

  // Opened imperatively, because `showModal()` is what gives a dialog its backdrop and focus trap;
  // the `open` attribute alone produces a non-modal box that leaves the page behind it usable.
  useEffect(() => {
    const element = dialog.current;

    if (element && !element.open) element.showModal();
  }, []);

  const detail = useQuery({
    queryKey: ['event', eventId],
    queryFn: () => getEvent(eventId!),
    enabled: eventId !== null,
  });

  // Fetched rather than hardcoded: the swatches somebody picks from and the values the server
  // accepts are then the same list, and changing the palette is one deploy.
  const palette = useQuery({
    queryKey: ['event-colors'],
    queryFn: listColors,
    staleTime: 60 * 60 * 1000,
  });

  const writable = useMemo(
    () =>
      calendars.filter((calendar) => calendar.access === 'WRITE' || calendar.access === 'MANAGE'),
    [calendars],
  );

  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [location, setLocation] = useState('');
  const [conferenceUrl, setConferenceUrl] = useState('');
  const [startsAt, setStartsAt] = useState('');
  const [endsAt, setEndsAt] = useState('');
  const [allDay, setAllDay] = useState(false);
  const [calendarId, setCalendarId] = useState('');
  const [status, setStatus] = useState<EventStatus>('CONFIRMED');
  const [visibility, setVisibility] = useState<EventVisibility>('DEFAULT');
  const [busy, setBusy] = useState(true);
  const [guestsCanInvite, setGuestsCanInvite] = useState(false);
  const [guestsCanModify, setGuestsCanModify] = useState(false);
  const [guestsCanSeeOthers, setGuestsCanSeeOthers] = useState(true);
  const [repeats, setRepeats] = useState(false);
  const [frequency, setFrequency] = useState<Frequency>('WEEKLY');
  const [interval, setIntervalValue] = useState(1);
  const [weekdays, setWeekdays] = useState<number[]>([]);
  const [ends, setEnds] = useState<'NEVER' | 'AFTER' | 'ON'>('NEVER');
  const [count, setCount] = useState(10);
  const [until, setUntil] = useState('');
  const [reminders, setReminders] = useState<number[]>([]);
  const [color, setColor] = useState<string | null>(null);
  const [guest, setGuest] = useState('');
  const [guestOptional, setGuestOptional] = useState(false);
  const [scope, setScope] = useState<SeriesScope>('THIS');
  const [confirming, setConfirming] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [tab, setTab] = useState<'when' | 'who' | 'more'>('when');

  const event = detail.data ?? null;
  const isSeries = event?.recurrence !== null && event?.recurrence !== undefined;

  // Fills the form from whichever source this dialog has: a loaded event, or the draft range the
  // person dragged out on the grid.
  useEffect(() => {
    if (event) {
      setTitle(event.title);
      setDescription(event.description ?? '');
      setLocation(event.location ?? '');
      setConferenceUrl(event.conferenceUrl ?? '');
      setStartsAt(toLocalInput(event.startsAt));
      setEndsAt(toLocalInput(event.endsAt));
      setAllDay(event.allDay);
      setCalendarId(event.calendarId ?? '');
      setStatus(event.status);
      setVisibility(event.visibility);
      setBusy(event.transparency === 'OPAQUE');
      setGuestsCanInvite(event.guestsCanInvite);
      setGuestsCanModify(event.guestsCanModify);
      setGuestsCanSeeOthers(event.guestsCanSeeOthers);
      setColor(event.color);
      setRepeats(event.recurrence !== null);

      if (event.recurrence) {
        setFrequency(event.recurrence.frequency);
        setIntervalValue(event.recurrence.interval);
        setWeekdays(event.recurrence.weekdays);
        setEnds(event.recurrence.ends);
        setCount(event.recurrence.count ?? 10);
        setUntil(event.recurrence.until ? event.recurrence.until.slice(0, 10) : '');
      }

      setError(null);

      return;
    }

    if (draft) {
      setTitle('');
      setDescription('');
      setLocation('');
      setConferenceUrl('');
      setStartsAt(toLocalInput(draft.startsAt.toISOString()));
      setEndsAt(toLocalInput(draft.endsAt.toISOString()));
      setAllDay(draft.allDay ?? false);
      setCalendarId(draft.calendarId ?? writable[0]?.id ?? '');
      setStatus('CONFIRMED');
      setVisibility('DEFAULT');
      setBusy(true);
      setColor(null);
      setRepeats(false);
      setWeekdays([draft.startsAt.getDay()]);
      setEnds('NEVER');
      setReminders([]);
      setError(null);
    }
  }, [event, draft, writable]);

  const refresh = (): void => {
    void client.invalidateQueries({ queryKey: ['calendar-window'] });
    void client.invalidateQueries({ queryKey: ['agenda'] });
    if (eventId) void client.invalidateQueries({ queryKey: ['event', eventId] });
  };

  const ruleOf = (): RuleInput | null => {
    if (!repeats) return null;

    return {
      frequency,
      interval,
      ...(frequency === 'WEEKLY' && weekdays.length > 0 ? { weekdays } : {}),
      ...(ends === 'AFTER' ? { count } : {}),
      ...(ends === 'ON' && until ? { until: new Date(`${until}T23:59:59`).toISOString() } : {}),
    };
  };

  const save = useMutation({
    mutationFn: () => {
      const body = {
        title: title.trim(),
        description: description.trim() === '' ? null : description.trim(),
        location: location.trim() === '' ? null : location.trim(),
        conferenceUrl: conferenceUrl.trim() === '' ? null : conferenceUrl.trim(),
        startsAt: fromLocalInput(startsAt),
        endsAt: fromLocalInput(endsAt),
        allDay,
        status,
        visibility,
        transparency: busy ? ('OPAQUE' as const) : ('TRANSPARENT' as const),
        guestsCanInvite,
        guestsCanModify,
        guestsCanSeeOthers,
        color,
        recurrence: ruleOf(),
        ...(calendarId ? { calendarId } : {}),
        ...(reminders.length > 0 ? { reminders } : {}),
      };

      if (!eventId) return createEvent(body);

      /*
       * A change to one occurrence carries both the scope and which occurrence was clicked.
       * `occurrenceStart` comes from the block the person opened, not from the form: the form's
       * start is where they are moving it *to*.
       */
      return updateEvent(eventId, {
        ...body,
        ...(isSeries
          ? { scope, ...(event?.occurrenceStart ? { occurrenceStart: event.occurrenceStart } : {}) }
          : {}),
      });
    },
    onSuccess: () => {
      setError(null);
      refresh();
      onClose();
    },
    onError: (caught: unknown) => setError(messageFor(caught)),
  });

  const remove = useMutation({
    mutationFn: () =>
      deleteEvent(eventId!, {
        ...(isSeries
          ? { scope, ...(event?.occurrenceStart ? { occurrenceStart: event.occurrenceStart } : {}) }
          : {}),
      }),
    onSuccess: () => {
      refresh();
      onClose();
    },
    onError: (caught: unknown) => setError(messageFor(caught)),
  });

  const invite = useMutation({
    mutationFn: () =>
      inviteGuests(eventId!, [
        guest.includes('@')
          ? { email: guest.trim(), optional: guestOptional }
          : { userId: guest, optional: guestOptional },
      ]),
    onSuccess: () => {
      setGuest('');
      setGuestOptional(false);
      setError(null);
      refresh();
    },
    onError: (caught: unknown) => setError(messageFor(caught)),
  });

  const accept = useMutation({
    mutationFn: (attendeeId: string) => acceptProposal(eventId!, attendeeId),
    onSuccess: () => {
      setError(null);
      refresh();
    },
    onError: (caught: unknown) => setError(messageFor(caught)),
  });

  const answer = useMutation({
    mutationFn: (response: EventDetail['attendees'][number]['response']) =>
      respondToEvent(eventId!, { response }),
    onSuccess: refresh,
    onError: (caught: unknown) => setError(messageFor(caught)),
  });

  const readOnly = eventId !== null && event !== null && !event.canEdit;
  const working = save.isPending || remove.isPending;

  return (
    <dialog
      ref={dialog}
      className="confirm confirm--wide event-dialog"
      aria-label={t('calendar.event')}
      // Escape and the backdrop both reach `onCancel`; both mean "leave it alone".
      onCancel={(cancelEvent) => {
        cancelEvent.preventDefault();
        onClose();
      }}
      onClose={onClose}
      onClick={(clickEvent) => {
        // A click on the backdrop lands on the dialog element itself rather than on its contents.
        if (clickEvent.target === dialog.current) onClose();
      }}
    >
      <header className="editor__head">
        <h2>{eventId ? t('calendar.event') : t('calendar.newEvent')}</h2>
        <button
          type="button"
          className="button button--quiet button--compact"
          onClick={onClose}
          aria-label={t('common.close')}
        >
          ×
        </button>
      </header>

      {/* The series question, asked before anything is changed rather than after. */}
      {isSeries && !readOnly && (
        <fieldset className="stack__field">
          <legend className="small strong">{t('calendar.seriesScope')}</legend>

          <div className="cell-inline">
            {(['THIS', 'FOLLOWING', 'ALL'] as const).map((option) => (
              <button
                key={option}
                type="button"
                className={`chip${scope === option ? ' chip--active' : ''}`}
                aria-pressed={scope === option}
                onClick={() => setScope(option)}
              >
                {t(`calendar.scope.${option}`)}
              </button>
            ))}
          </div>
        </fieldset>
      )}

      <nav className="tabs" aria-label={t('calendar.event')}>
        {(['when', 'who', 'more'] as const).map((key) => (
          <button
            key={key}
            type="button"
            className={`tab${tab === key ? ' is-active' : ''}`}
            onClick={() => setTab(key)}
          >
            {t(`calendar.tab.${key}`)}
          </button>
        ))}
      </nav>

      <form
        className="stack"
        onSubmit={(formEvent) => {
          formEvent.preventDefault();
          if (title.trim()) save.mutate();
        }}
      >
        <label className="field">
          <span>{t('calendar.eventTitle')}</span>
          <input
            value={title}
            maxLength={300}
            disabled={readOnly}
            placeholder={t('calendar.titlePlaceholder')}
            onChange={(e) => setTitle(e.target.value)}
          />
        </label>

        {tab === 'when' && (
          <>
            <div className="form-grid">
              <label className="field">
                <span>{t('calendar.starts')}</span>
                <input
                  type={allDay ? 'date' : 'datetime-local'}
                  value={allDay ? startsAt.slice(0, 10) : startsAt}
                  disabled={readOnly}
                  onChange={(e) => setStartsAt(allDay ? `${e.target.value}T00:00` : e.target.value)}
                />
              </label>

              <label className="field">
                <span>{t('calendar.ends')}</span>
                <input
                  type={allDay ? 'date' : 'datetime-local'}
                  value={allDay ? endsAt.slice(0, 10) : endsAt}
                  disabled={readOnly}
                  onChange={(e) => setEndsAt(allDay ? `${e.target.value}T23:59` : e.target.value)}
                />
              </label>
            </div>

            <label className="prefs__switch">
              <input
                type="checkbox"
                checked={allDay}
                disabled={readOnly}
                onChange={(e) => setAllDay(e.target.checked)}
              />
              <span className="prefs__switch-text">
                <span>{t('calendar.allDay')}</span>
              </span>
            </label>

            <label className="prefs__switch">
              <input
                type="checkbox"
                checked={repeats}
                disabled={readOnly}
                onChange={(e) => setRepeats(e.target.checked)}
              />
              <span className="prefs__switch-text">
                <span>{t('calendar.repeats')}</span>
              </span>
            </label>

            {repeats && (
              <div className="stack recurrence">
                <div className="form-grid">
                  <label className="field">
                    <span>{t('calendar.frequency')}</span>
                    <select
                      className="select"
                      value={frequency}
                      disabled={readOnly}
                      onChange={(e) => setFrequency(e.target.value as Frequency)}
                    >
                      {(['DAILY', 'WEEKLY', 'MONTHLY', 'YEARLY'] as const).map((value) => (
                        <option key={value} value={value}>
                          {t(`calendar.frequency.${value}`)}
                        </option>
                      ))}
                    </select>
                  </label>

                  <label className="field">
                    <span>{t('calendar.every')}</span>
                    <input
                      type="number"
                      min={1}
                      max={365}
                      value={interval}
                      disabled={readOnly}
                      onChange={(e) => setIntervalValue(Math.max(1, Number(e.target.value)))}
                    />
                  </label>
                </div>

                {frequency === 'WEEKLY' && (
                  <div className="cell-inline">
                    {[0, 1, 2, 3, 4, 5, 6].map((weekday) => (
                      <button
                        key={weekday}
                        type="button"
                        className={`chip${weekdays.includes(weekday) ? ' chip--active' : ''}`}
                        aria-pressed={weekdays.includes(weekday)}
                        disabled={readOnly}
                        onClick={() =>
                          setWeekdays((current) =>
                            current.includes(weekday)
                              ? current.filter((day) => day !== weekday)
                              : [...current, weekday].sort(),
                          )
                        }
                      >
                        {t(`calendar.weekday.${weekday}`)}
                      </button>
                    ))}
                  </div>
                )}

                <div className="form-grid">
                  <label className="field">
                    <span>{t('calendar.endsWhen')}</span>
                    <select
                      className="select"
                      value={ends}
                      disabled={readOnly}
                      onChange={(e) => setEnds(e.target.value as typeof ends)}
                    >
                      <option value="NEVER">{t('calendar.ends.NEVER')}</option>
                      <option value="AFTER">{t('calendar.ends.AFTER')}</option>
                      <option value="ON">{t('calendar.ends.ON')}</option>
                    </select>
                  </label>

                  {ends === 'AFTER' && (
                    <label className="field">
                      <span>{t('calendar.occurrences')}</span>
                      <input
                        type="number"
                        min={1}
                        max={730}
                        value={count}
                        disabled={readOnly}
                        onChange={(e) => setCount(Math.max(1, Number(e.target.value)))}
                      />
                    </label>
                  )}

                  {ends === 'ON' && (
                    <label className="field">
                      <span>{t('calendar.untilDate')}</span>
                      <input
                        type="date"
                        value={until}
                        disabled={readOnly}
                        onChange={(e) => setUntil(e.target.value)}
                      />
                    </label>
                  )}
                </div>
              </div>
            )}

            <fieldset className="stack__field">
              <legend className="small strong">{t('calendar.color')}</legend>

              <div className="swatches">
                <button
                  type="button"
                  className={`swatch swatch--auto${color === null ? ' is-selected' : ''}`}
                  aria-pressed={color === null}
                  title={t('calendar.colorFromCalendar')}
                  disabled={readOnly}
                  onClick={() => setColor(null)}
                >
                  {'—'}
                </button>

                {(palette.data?.colors ?? []).map((option) => (
                  <button
                    key={option.key}
                    type="button"
                    className={`swatch${color === option.hex ? ' is-selected' : ''}`}
                    style={{ background: option.hex }}
                    aria-pressed={color === option.hex}
                    aria-label={t(`calendar.color.${option.key}`)}
                    title={t(`calendar.color.${option.key}`)}
                    disabled={readOnly}
                    onClick={() => setColor(option.hex)}
                  />
                ))}
              </div>
            </fieldset>

            <label className="field">
              <span>{t('calendar.calendar')}</span>
              <select
                className="select"
                value={calendarId}
                disabled={readOnly || writable.length === 0}
                onChange={(e) => setCalendarId(e.target.value)}
              >
                {writable.map((calendar) => (
                  <option key={calendar.id} value={calendar.id}>
                    {calendar.name}
                  </option>
                ))}
              </select>
            </label>
          </>
        )}

        {tab === 'who' && (
          <>
            <label className="field">
              <span>{t('calendar.location')}</span>
              <input
                value={location}
                maxLength={500}
                disabled={readOnly}
                placeholder={t('calendar.locationPlaceholder')}
                onChange={(e) => setLocation(e.target.value)}
              />
            </label>

            <label className="field">
              <span>{t('calendar.conference')}</span>
              <input
                type="url"
                value={conferenceUrl}
                maxLength={2000}
                disabled={readOnly}
                placeholder={t('calendar.conferencePlaceholder')}
                onChange={(e) => setConferenceUrl(e.target.value)}
              />
            </label>

            {eventId && event && (
              <section className="editor__section">
                <h3>{t('calendar.attendees')}</h3>

                {event.attendees.length === 0 ? (
                  <p className="muted small">{t('calendar.noAttendees')}</p>
                ) : (
                  <ul className="home-list">
                    {event.attendees.map((attendee) => (
                      <li key={attendee.id}>
                        <span className="cell-inline">
                          <span>{attendee.name ?? attendee.email ?? attendee.userId ?? '—'}</span>
                          {attendee.optional && (
                            <span className="muted small">{t('calendar.optional')}</span>
                          )}
                          <Badge
                            tone={
                              attendee.response === 'ACCEPTED'
                                ? 'positive'
                                : attendee.response === 'DECLINED'
                                  ? 'danger'
                                  : attendee.response === 'TENTATIVE'
                                    ? 'warning'
                                    : 'neutral'
                            }
                          >
                            {t(`calendar.response.${attendee.response}`)}
                          </Badge>
                        </span>
                        {attendee.comment && (
                          <span className="muted small">{attendee.comment}</span>
                        )}

                        {/*
                          A guest proposed another time. Accepting is an ordinary edit made by
                          somebody who may make one, so it moves the meeting and tells everybody —
                          which is why the button is only here for people who can edit it.
                        */}
                        {attendee.proposedStartsAt && event.canEdit && (
                          <span className="cell-inline">
                            <span className="muted small">
                              {t('calendar.proposed').replace(
                                '{when}',
                                new Date(attendee.proposedStartsAt).toLocaleString(),
                              )}
                            </span>
                            <button
                              type="button"
                              className="button button--quiet button--compact"
                              disabled={accept.isPending}
                              onClick={() => accept.mutate(attendee.id)}
                            >
                              {t('calendar.acceptProposal')}
                            </button>
                          </span>
                        )}
                      </li>
                    ))}
                  </ul>
                )}

                {event.canInvite && (
                  <div className="stack">
                    <div className="cell-inline">
                      <MemberSelect
                        value={guest.includes('@') ? null : guest || null}
                        placeholder={t('calendar.inviteColleague')}
                        onChange={(next) => setGuest(next ?? '')}
                      />
                      <input
                        type="email"
                        value={guest.includes('@') ? guest : ''}
                        placeholder={t('calendar.invitePlaceholder')}
                        onChange={(e) => setGuest(e.target.value)}
                      />
                      <label className="cell-inline small">
                        <input
                          type="checkbox"
                          checked={guestOptional}
                          onChange={(e) => setGuestOptional(e.target.checked)}
                        />
                        {t('calendar.optional')}
                      </label>
                      <button
                        type="button"
                        className="button button--quiet button--compact"
                        disabled={guest === '' || invite.isPending}
                        onClick={() => invite.mutate()}
                      >
                        {t('calendar.invite')}
                      </button>
                    </div>
                  </div>
                )}

                {/* Answering is not editing: a guest with no write access still says yes or no. */}
                <div className="cell-inline">
                  <span className="muted small">{t('calendar.yourAnswer')}</span>
                  {(['ACCEPTED', 'TENTATIVE', 'DECLINED'] as const).map((response) => (
                    <button
                      key={response}
                      type="button"
                      className="chip"
                      disabled={answer.isPending}
                      onClick={() => answer.mutate(response)}
                    >
                      {t(`calendar.response.${response}`)}
                    </button>
                  ))}
                </div>
              </section>
            )}
          </>
        )}

        {tab === 'more' && (
          <>
            <label className="field">
              <span>{t('calendar.notes')}</span>
              <textarea
                rows={4}
                value={description}
                disabled={readOnly}
                onChange={(e) => setDescription(e.target.value)}
              />
            </label>

            <div className="form-grid">
              <label className="field">
                <span>{t('crm.field.status')}</span>
                <select
                  className="select"
                  value={status}
                  disabled={readOnly}
                  onChange={(e) => setStatus(e.target.value as EventStatus)}
                >
                  {(['CONFIRMED', 'TENTATIVE', 'CANCELED'] as const).map((value) => (
                    <option key={value} value={value}>
                      {t(`calendar.status.${value}`)}
                    </option>
                  ))}
                </select>
              </label>

              <label className="field">
                <span>{t('calendar.visibility')}</span>
                <select
                  className="select"
                  value={visibility}
                  disabled={readOnly}
                  onChange={(e) => setVisibility(e.target.value as EventVisibility)}
                >
                  {(['DEFAULT', 'PUBLIC', 'PRIVATE'] as const).map((value) => (
                    <option key={value} value={value}>
                      {t(`calendar.visibility.${value}`)}
                    </option>
                  ))}
                </select>
              </label>
            </div>

            <label className="prefs__switch">
              <input
                type="checkbox"
                checked={busy}
                disabled={readOnly}
                onChange={(e) => setBusy(e.target.checked)}
              />
              <span className="prefs__switch-text">
                <span>{t('calendar.showAsBusy')}</span>
                <span className="muted small">{t('calendar.showAsBusyHint')}</span>
              </span>
            </label>

            <fieldset className="stack__field">
              <legend className="small strong">{t('calendar.guestPermissions')}</legend>

              {(
                [
                  ['guestsCanModify', guestsCanModify, setGuestsCanModify],
                  ['guestsCanInvite', guestsCanInvite, setGuestsCanInvite],
                  ['guestsCanSeeOthers', guestsCanSeeOthers, setGuestsCanSeeOthers],
                ] as const
              ).map(([key, value, set]) => (
                <label key={key} className="prefs__switch">
                  <input
                    type="checkbox"
                    checked={value}
                    disabled={readOnly}
                    onChange={(e) => set(e.target.checked)}
                  />
                  <span className="prefs__switch-text">
                    <span>{t(`calendar.${key}`)}</span>
                  </span>
                </label>
              ))}
            </fieldset>

            <fieldset className="stack__field">
              <legend className="small strong">{t('calendar.reminders')}</legend>

              <div className="cell-inline">
                {[0, 10, 30, 60, 24 * 60].map((minutes) => (
                  <button
                    key={minutes}
                    type="button"
                    className={`chip${reminders.includes(minutes) ? ' chip--active' : ''}`}
                    aria-pressed={reminders.includes(minutes)}
                    disabled={readOnly}
                    onClick={() =>
                      setReminders((current) =>
                        current.includes(minutes)
                          ? current.filter((value) => value !== minutes)
                          : [...current, minutes],
                      )
                    }
                  >
                    {t(`calendar.remind.${minutes}`)}
                  </button>
                ))}
              </div>
            </fieldset>
          </>
        )}

        {error && (
          <p className="alert" role="alert">
            {error}
          </p>
        )}

        <div className="editor__actions">
          {!readOnly && (
            <button
              type="submit"
              className="button button--primary"
              disabled={working || title.trim() === ''}
            >
              {save.isPending ? t('common.saving') : t('common.save')}
            </button>
          )}

          {eventId && event?.canRemove && (
            <>
              {confirming ? (
                <>
                  <button
                    type="button"
                    className="button button--danger button--compact"
                    disabled={working}
                    onClick={() => remove.mutate()}
                  >
                    {t('calendar.confirmDelete')}
                  </button>
                  <button
                    type="button"
                    className="button button--quiet button--compact"
                    onClick={() => setConfirming(false)}
                  >
                    {t('confirm.cancel')}
                  </button>
                </>
              ) : (
                <button
                  type="button"
                  className="button button--quiet button--compact"
                  onClick={() => setConfirming(true)}
                >
                  {t('calendar.deleteEvent')}
                </button>
              )}
            </>
          )}
        </div>

        {readOnly && <p className="muted small">{t('calendar.readOnly')}</p>}
      </form>
    </dialog>
  );
}

/**
 * An instant as `datetime-local` wants it: the browser's own zone, no offset, no seconds.
 *
 * Built from the parts rather than by slicing `toISOString()`, which would hand the input a UTC
 * wall clock and move every meeting by the offset.
 */
function toLocalInput(iso: string): string {
  const date = new Date(iso);

  if (Number.isNaN(date.getTime())) return '';

  const pad = (value: number): string => String(value).padStart(2, '0');

  return (
    `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}` +
    `T${pad(date.getHours())}:${pad(date.getMinutes())}`
  );
}

/** And back: the local wall clock the person typed, as the instant it names. */
function fromLocalInput(value: string): string {
  return new Date(value).toISOString();
}
